import { useEffect, useMemo, useState } from 'react';

import {
  AddIcon,
  Button,
  DateRangePicker,
  Divider,
  Input,
  Select,
  Text,
  TypeSelectOption,
  dialogService,
} from '@thefarmersfront/kitchen';

import dayjs from 'dayjs';

import { PageTitle, SearchFilter, Table, TableBottomField, TableTopField } from '~/components/common';
import { PageWrapper } from '~/components/layout';
import { StockReleaseDetailModal, StockReleaseRegisterModal } from '~/components/stock';

import { useModal, useStableCallback, useToggle } from '~/hooks/common';
import {
  useGetVendorReleaseDetail,
  useOption,
  usePatchVendorReleaseStatus,
  usePostVendorReleaseSearch,
} from '~/hooks/stock';
import { getDateByOffset, getStockReleaseColumnDefs } from '~/utils';

import {
  BREAD_CRUMB,
  DEFAULT_INPUT_STATUS,
  ERROR_STATUS,
  INITIAL_STOCK_PAGE_OFFSET,
  INITIAL_STOCK_PAGE_SIZE,
  OPTION_ALL,
  PATH,
  STOCK_CODES,
  STOCK_PAGE_SIZE,
  STOCK_RELEASE_CANCEL_ALLOW_CODES,
  STOCK_RELEASE_MAX_SEARCH_PERIOD_DAYS,
} from '~/constants';

import { TypeInputState } from '~/types/common.types';
import type { TypeStockReleaseFilter, TypeStockReleaseInfo } from '~/types/stock.types';

import styles from './StockReleasePage.module.scss';

const INITIAL_STOCK_RELEASE_FILTER: TypeStockReleaseFilter = {
  /** 검색 키워드 유형 */
  keywordType: null,
  /** 검색 키워드 값 */
  keywordValue: '',
  /** 반출 출발지 센터 */
  cluster: OPTION_ALL,
  /** 반출 상태 */
  status: OPTION_ALL,
  /** 반출 방식 */
  method: OPTION_ALL,
  /** 반출 희망일 - 기본값: 접속일 ~ D+6 */
  expectedDate: { start: new Date(), end: getDateByOffset(6, 'day') },
  /** 요청 등록일 */
  createdDate: null,
  /** 페이지 사이즈 */
  pageSize: INITIAL_STOCK_PAGE_SIZE,
  /** 페이지 번호 */
  currentPage: INITIAL_STOCK_PAGE_OFFSET,
};

const StockReleasePage = () => {
  const { isToggle: isFilterOpen, handleToggle: handleFilterToggle } = useToggle({ initialState: true });

  const { options } = useOption({
    codes: [
      STOCK_CODES.vendorReleaseCluster,
      STOCK_CODES.vendorReleaseKeyword,
      STOCK_CODES.vendorReleaseStatus,
      STOCK_CODES.vendorReleaseMethod,
    ],
  });
  const filterOptions = useMemo(() => {
    return {
      cluster: [OPTION_ALL, ...(options?.vendorReleaseCluster ?? [])],
      keywordType: options?.vendorReleaseKeyword ?? [],
      status: [OPTION_ALL, ...(options?.vendorReleaseStatus ?? [])],
      method: [OPTION_ALL, ...(options?.vendorReleaseMethod ?? [])],
    };
  }, [options]);

  const { requestVendorRelease, responseData: stockReleaseListData } = usePostVendorReleaseSearch();
  const { requestVendorReleaseCancel } = usePatchVendorReleaseStatus();
  const { requestVendorReleaseDetail, responseData: stockReleaseDetailData } = useGetVendorReleaseDetail();

  const registerModal = useModal({
    onConfirmCompleted: () => {
      requestSearch();
    },
  });
  const detailModal = useModal();

  const handleClickViewDetail = useStableCallback((id: number) => {
    requestVendorReleaseDetail(id, {
      onSuccess: () => {
        detailModal.handleOpenModal();
      },
    });
  });
  const { columnDefs } = useMemo(
    () => getStockReleaseColumnDefs({ onClickViewDetail: handleClickViewDetail }),
    [handleClickViewDetail]
  );

  const [stockReleaseFilter, setStockReleaseFilter] = useState<TypeStockReleaseFilter>(INITIAL_STOCK_RELEASE_FILTER);
  const [selectedItems, setSelectedItems] = useState<TypeStockReleaseInfo[]>([]);
  const [filterStatus, setFilterStatus] = useState<Record<string, TypeInputState>>({});

  // 검색 필터 값 변경
  const handleChangeFilterValues = (values: Partial<TypeStockReleaseFilter>) => {
    setStockReleaseFilter((prev) => ({ ...prev, ...values }));
  };

  // 검색 필터 상태 변경
  const handleChangeFilterStatus = (filedStatus: Record<string, TypeInputState>) => {
    setFilterStatus((prev) => ({ ...prev, ...filedStatus }));
  };

  // 검색 실행
  const requestSearch = (filterValues: Partial<TypeStockReleaseFilter> = {}) => {
    const filterState = { ...stockReleaseFilter, ...filterValues };

    // 반출 희망일 or 요청 등록일 입력 검증
    const isEmptyExpectedDate = !(filterState.expectedDate?.start && filterState.expectedDate?.end);
    const isEmptyCreatedDate = !(filterState.createdDate?.start && filterState.createdDate?.end);

    if (isEmptyExpectedDate && isEmptyCreatedDate) {
      dialogService.addDialog({
        children: `'반출 희망일'과 '요청 등록일' 둘 중에\n최소 하나는 입력해야 검색이 가능합니다.`,
      });
      handleChangeFilterStatus({ expectedDate: ERROR_STATUS, createdDate: ERROR_STATUS });
      return;
    }

    const validateDateRange = (
      dateRange: { start: Date | null; end: Date | null } | null,
      fieldName: string
    ): boolean => {
      const isEmpty = !(dateRange?.start && dateRange?.end);
      if (isEmpty) return true;

      const isInPeriod = dayjs(dateRange.start).add(STOCK_RELEASE_MAX_SEARCH_PERIOD_DAYS, 'day').isAfter(dateRange.end);

      if (!isInPeriod) {
        dialogService.addDialog({
          children: `최대 ${STOCK_RELEASE_MAX_SEARCH_PERIOD_DAYS}일 기간으로 입력 후 검색해 주세요.`,
        });
        handleChangeFilterStatus({ [fieldName]: ERROR_STATUS });
        return false;
      }
      return true;
    };

    // 반출 희망일 기간 검증
    if (!validateDateRange(filterState.expectedDate, 'expectedDate')) return;

    // 요청 등록일 기간 검증
    if (!validateDateRange(filterState.createdDate, 'createdDate')) return;

    requestVendorRelease(filterState, {
      onSuccess: () => {
        setStockReleaseFilter(filterState);
      },
    });
  };

  // 페이지 사이즈 변경
  const handleChangePageLimit = (option: TypeSelectOption<number>) => {
    requestSearch({ pageSize: option.value, currentPage: INITIAL_STOCK_PAGE_OFFSET });
  };

  // 페이지네이션 변경
  const handleClickPage = (currentPage: number) => {
    requestSearch({ currentPage });
  };

  // 리스트 선택 변경
  const handleSelectionChange = (selectedRows: TypeStockReleaseInfo[]) => {
    setSelectedItems(selectedRows);
  };

  // 필터 검색 버튼 클릭
  const handleSubmit = () => {
    requestSearch({ currentPage: INITIAL_STOCK_PAGE_OFFSET });
  };

  // 필터 초기화 버튼 클릭
  const handleClickFilterReset = () => {
    setStockReleaseFilter({
      ...INITIAL_STOCK_RELEASE_FILTER,
      keywordType: filterOptions.keywordType[0],
    });
  };

  // 반출 취소 버튼 클릭
  const handleClickCancel = () => {
    const isValidStatus = selectedItems.every(({ statusCode }) =>
      STOCK_RELEASE_CANCEL_ALLOW_CODES.includes(statusCode)
    );
    if (!isValidStatus) {
      dialogService.addDialog({
        children: '반출요청 상태의 반출요청번호만 취소할 수 있습니다.',
      });
      return;
    }

    dialogService.addDialog({
      type: 'confirm',
      children: `${selectedItems.length}개의 반출 요청번호를 취소하시겠습니까?`,
      onConfirm: () => {
        requestVendorReleaseCancel(
          selectedItems.map((item) => item.id),
          {
            onSuccess: () => {
              requestSearch();
              setSelectedItems([]);
            },
          }
        );
      },
    });
  };

  useEffect(() => {
    if (filterOptions.keywordType) {
      setStockReleaseFilter((prev) => ({
        ...prev,
        keywordType: filterOptions.keywordType[0],
      }));
    }
  }, [filterOptions.keywordType]);

  return (
    <PageWrapper>
      <PageTitle
        title="반출 요청 관리"
        breadcrumbs={BREAD_CRUMB[PATH.STOCK_RELEASE]}
        handleFilterToggle={handleFilterToggle}
      />
      {isFilterOpen && (
        <>
          <SearchFilter formId="stock-release-search-form" onSubmit={handleSubmit} onReset={handleClickFilterReset}>
            <div className={`${styles['filter-wrapper']} ${styles['select-input-field']}`}>
              <Text className={styles['filter-label']} size="small" weight="semi-bold">
                키워드
              </Text>
              <div className={styles['select-input-container']}>
                <Select
                  className={styles['input-select']}
                  id="keywordType"
                  size="small"
                  disabled={!filterOptions.keywordType.length}
                  options={filterOptions.keywordType}
                  value={stockReleaseFilter.keywordType}
                  onChange={(_: unknown, keywordType: TypeSelectOption<string>) =>
                    handleChangeFilterValues({ keywordType, keywordValue: '' })
                  }
                />
                <Input
                  type="text"
                  id="keywordValue"
                  size="small"
                  value={stockReleaseFilter.keywordValue}
                  placeholder="입력"
                  onChange={(e) => handleChangeFilterValues({ keywordValue: e.target.value })}
                  onBlur={(e) => handleChangeFilterValues({ keywordValue: e.target.value.trim() })}
                />
              </div>
            </div>
            <div className={styles['filter-wrapper']}>
              <Text className={styles['filter-label']} size="small" weight="semi-bold">
                반출 출발지 센터
              </Text>
              <Select
                id="cluster"
                size="small"
                options={filterOptions.cluster}
                value={stockReleaseFilter.cluster}
                onChange={(_: unknown, cluster: TypeSelectOption<string>) => handleChangeFilterValues({ cluster })}
              />
            </div>
            <div className={styles['filter-wrapper']}>
              <Text className={styles['filter-label']} size="small" weight="semi-bold">
                반출 상태
              </Text>
              <Select
                id="status"
                size="small"
                options={filterOptions.status}
                value={stockReleaseFilter.status}
                onChange={(_: unknown, status: TypeSelectOption<string>) => handleChangeFilterValues({ status })}
              />
            </div>
            <div className={styles['filter-wrapper']}>
              <Text className={styles['filter-label']} size="small" weight="semi-bold">
                반출 방식
              </Text>
              <Select
                id="method"
                size="small"
                options={filterOptions.method}
                value={stockReleaseFilter.method}
                onChange={(_: unknown, method: TypeSelectOption<string>) => handleChangeFilterValues({ method })}
              />
            </div>
            <div className={`${styles['filter-wrapper']} ${styles['date-range-field']}`}>
              <Text className={styles['filter-label']} size="small" weight="semi-bold">
                반출 희망일
              </Text>
              <DateRangePicker
                size="small"
                placeholder={{ start: '시작일', end: '종료일' }}
                value={stockReleaseFilter.expectedDate}
                status={filterStatus?.expectedDate?.status}
                onChange={(expectedDate) => handleChangeFilterValues({ expectedDate })}
                onFocus={() =>
                  handleChangeFilterStatus({ expectedDate: DEFAULT_INPUT_STATUS, createdDate: DEFAULT_INPUT_STATUS })
                }
              />
            </div>
            <div className={`${styles['filter-wrapper']} ${styles['date-range-field']}`}>
              <Text className={styles['filter-label']} size="small" weight="semi-bold">
                요청 등록일
              </Text>
              <DateRangePicker
                size="small"
                placeholder={{ start: '시작일', end: '종료일' }}
                value={stockReleaseFilter.createdDate}
                status={filterStatus?.createdDate?.status}
                onChange={(createdDate) => handleChangeFilterValues({ createdDate })}
                onFocus={() =>
                  handleChangeFilterStatus({ expectedDate: DEFAULT_INPUT_STATUS, createdDate: DEFAULT_INPUT_STATUS })
                }
              />
            </div>
          </SearchFilter>
          <Divider color="gray100" />
        </>
      )}
      <div className={styles['table-section']}>
        <TableTopField
          dataTotalCount={stockReleaseListData?.pagination.listTotal ?? 0}
          selectedCount={selectedItems.length}
          rightButton={
            <>
              <Button
                size="tiny"
                variant="outlined"
                status="error"
                disabled={selectedItems.length === 0}
                onClick={handleClickCancel}
              >
                반출 취소
              </Button>
              <Divider className={styles['divider']} orientation="vertical" color="gray100" />
              <Button
                size="tiny"
                color="secondary"
                leftIcon={<AddIcon size={16} />}
                onClick={registerModal.handleOpenModal}
              >
                반출 요청 등록
              </Button>
            </>
          }
        />
        <div className={styles['table-wrapper']}>
          <Table
            data={stockReleaseListData?.data ?? null}
            columns={columnDefs}
            rowStatusType={stockReleaseListData === null ? 'INIT' : 'EMPTY'}
            selection={{ onChange: handleSelectionChange }}
          />
        </div>
        <TableBottomField
          pageSizeOptions={Object.values(STOCK_PAGE_SIZE)}
          currentPage={stockReleaseFilter.currentPage}
          totalCount={stockReleaseListData?.pagination.listTotal ?? 0}
          pageSize={stockReleaseFilter.pageSize}
          onChangePageLimit={handleChangePageLimit}
          onClickPage={handleClickPage}
        />
      </div>
      {registerModal.isOpen && (
        <StockReleaseRegisterModal
          open={registerModal.isOpen}
          onCancel={registerModal.handleCloseModal}
          onConfirm={registerModal.handleConfirmModal}
        />
      )}
      {detailModal.isOpen && stockReleaseDetailData && (
        <StockReleaseDetailModal
          open={detailModal.isOpen}
          data={stockReleaseDetailData}
          onConfirm={detailModal.handleCloseModal}
        />
      )}
    </PageWrapper>
  );
};

export default StockReleasePage;
